import { Divider, Grid, Menu, MenuItem, Typography } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey } from "assets/jss/common-styles";
import ButtonEllipsis from "components/Buttons/ButtonEllipsis";
import IconButtonEdit from "components/Buttons/IconButtonEdit";
import IconEmail from "components/Icons/IconEmail";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiImage from "components/MuiExtras/MuiImage";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import MuiDanger from "components/MuiExtras/Typography/MuiDanger";
import MuiWarning from "components/MuiExtras/Typography/MuiWarning";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { AccountType } from "models/ApiEnums";
import { UserAdminListDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    menuShareAnchorEl: any;
    menuAnchorEl: any;

    mouseOver: boolean;
    dialog: any;
}

interface IProps {
    user: UserAdminListDto;
    onChangePassword?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    onRecover?: () => void;
    onViewProfile?: () => void;
}

type Props = IProps & WithSnackbar & WithTranslation & StyledComponentProps<FeedClassKey>;

/////////////////////////////////////////////////////////////////////////////////////

class UserListCard extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            mouseOver: false,
            menuAnchorEl: null,
            menuShareAnchorEl: null
        };
    }

    ///
    private handleEdit = () => {
        this.handleCloseMenu();
        this.props.onEdit();
    };

    ///
    private handleDelete = () => {
        this.handleCloseMenu();
        this.props.onDelete();
    };

    ///
    private handleRecover = () => {
        this.handleCloseMenu();
        this.props.onRecover();
    };

    ///
    private handlePassword = () => {
        this.handleCloseMenu();
        this.props.onChangePassword();
    };

    ///
    private handleProfile = () => {
        this.handleCloseMenu();
        this.props.onViewProfile();
    };

    ///
    private handleCloseMenu() {
        this.setState({ menuAnchorEl: null, mouseOver: false });
    }

    ///
    private buildOverflowMenu(): Array<any> {

        const actionMenu: Array<any> = [];

        if (this.props.user.deleted) {
            if (this.props.onRecover) actionMenu.push(<MenuItem key="recover" onClick={this.handleRecover}><MuiColor variant="error">{this.props.t("Views.User.Common.Actions.Recover")}</MuiColor></MenuItem>);
        }
        else {
            if (this.props.onEdit) actionMenu.push(<MenuItem key="edit" onClick={this.handleEdit}>{this.props.t("Views.User.Common.Actions.Edit")}</MenuItem>);
        }

        if (this.props.onViewProfile)
            actionMenu.push(<MenuItem key="profile" onClick={this.handleProfile}>{this.props.t("Views.User.Common.Actions.Profile")}</MenuItem>);

        if (actionMenu.length > 0) actionMenu.push(<Divider key="div1" />);

        if (this.props.onChangePassword && (this.props.user.accountType & AccountType.Local) > 0)
            actionMenu.push(<MenuItem key="password" onClick={this.handlePassword}>{this.props.t("Views.User.Common.Actions.ChangePassword")}</MenuItem>);

        if (!this.props.user.deleted && this.props.onDelete) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="div1" />);
            if (this.props.onDelete) actionMenu.push(<MenuItem key="delete" onClick={this.handleDelete}><MuiColor variant="error">{this.props.t("Views.User.Common.Actions.Delete")}</MuiColor></MenuItem>);
        }

        return actionMenu;
    }

    ///
    public render() {
        const {
            classes,
            user
        } = this.props;

        const { mouseOver } = this.state;

        const overflowActions = this.buildOverflowMenu();

        const thumbnail = <MuiImage size="sm" orientation="square" src={user.thumbnailUrl} />;
        const userName = <b>{user.name}</b>;
        const usenameBlock = user.blockReason ? <MuiWarning>{userName}</MuiWarning> : userName;

        return (
            <>
                <MuiPaper className={classes.card}
                    onMouseEnter={() => this.setState({ mouseOver: true })}
                    onMouseLeave={() => this.setState({ mouseOver: false })
                    }
                >
                    <Grid container spacing={2} >
                        <Grid item>
                            {
                                this.props.onViewProfile
                                    ? <MuiClickable onClick={this.props.onViewProfile}>{thumbnail}</MuiClickable>
                                    : thumbnail
                            }

                        </Grid>
                        <Grid item xs>

                            <Grid container>
                                <Grid item xs>
                                    <Typography variant="h6">
                                        {
                                            this.props.onEdit
                                                ? <MuiClickable onClick={this.props.onEdit}>{usenameBlock}</MuiClickable>
                                                : usenameBlock
                                        }
                                    </Typography>
                                    {
                                        (user.blockReason || user.isLockedOut) &&
                                        <Typography variant="subtitle1">
                                            {user.blockReason && <MuiWarning>{this.props.t("Views.User.Components.UserListCard.BlockedText")}</MuiWarning>}
                                            {user.isLockedOut && <MuiDanger>{this.props.t("Views.User.Components.UserListCard.LockedOutText")}</MuiDanger>}
                                        </Typography>
                                    }
                                </Grid>
                                <Grid item>
                                    <IconButtonEdit
                                        size="small"
                                        visible={mouseOver}
                                        onClick={this.handleEdit}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item className={classes.subtitle}>
                                    <IconEmail />&nbsp;<span>{user.email}</span>
                                </Grid>

                                <Grid item className={classes.cardActions}>
                                    <ButtonEllipsis
                                        onClick={(e) => { this.setState({ menuAnchorEl: e.currentTarget }); }}
                                    />
                                    <Menu
                                        open={this.state.menuAnchorEl != null}
                                        anchorEl={this.state.menuAnchorEl}
                                        onClose={() => { this.handleCloseMenu(); }}
                                    >
                                        {overflowActions}
                                    </Menu>
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>

                </MuiPaper>

                {this.state.dialog}
            </>
        );
    }
}

export default withSnackbar(withTranslation()(UserListCard));